import { useOutletContext, Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { usePageContent, Paragraphs } from "@/lib/usePageContent";
import { Button } from "@/components/ui/button";
import { Target, Eye, Globe2, HeartHandshake, ArrowRight } from "lucide-react";

const VALUES = [
  { icon: Globe2, title: "Open by default", text: "Every article is free to read, download and reuse — knowledge should not sit behind paywalls." },
  { icon: Eye, title: "Transparent review", text: "Fair, rigorous and accountable peer review at the heart of everything we publish." },
  { icon: HeartHandshake, title: "Author-first", text: "Clear timelines, retained copyright and responsive editorial support." },
  { icon: Target, title: "Scholarly integrity", text: "We uphold COPE ethical standards across authorship, data and conflicts of interest." },
];

export default function About() {
  const c = usePageContent("about");
  return (
    <div>
      <PageHeader eyebrow="About Us" title={c?.title || "About"} subtitle={c?.subtitle} breadcrumb="About" />

      <section className="max-w-4xl mx-auto px-6 py-16 space-y-5 text-slate-700 dark:text-slate-300 leading-relaxed [&>p:first-child]:text-lg">
        <Paragraphs text={c?.body} />
      </section>

      <section className="bg-slate-50 dark:bg-slate-900/40 border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <h2 className="font-serif font-bold text-3xl text-slate-900 dark:text-slate-100 mb-10 text-center">Our values</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6">
                <span className="grid place-items-center w-12 h-12 rounded-lg bg-primary/5 dark:bg-primary/20 text-primary dark:text-sky-300 mb-4"><v.icon className="w-6 h-6" /></span>
                <h3 className="font-serif font-bold text-lg text-slate-900 dark:text-slate-100">{v.title}</h3>
                <p className="text-sm text-slate-500 mt-2">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="relative overflow-hidden rounded-2xl bg-primary text-white p-10 text-center">
          <div className="absolute inset-0 hero-grid opacity-40" />
          <div className="relative">
            <h2 className="font-serif font-bold text-3xl">Join a global research community</h2>
            <p className="mt-3 text-slate-300">Whether you are an author, reviewer or reader, there's a place for you at Noventra.</p>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <Button asChild size="lg" className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"><Link to="/for-authors">Publish with us <ArrowRight className="w-4 h-4 ml-1" /></Link></Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-white/30 bg-white/5 text-white hover:bg-white/15"><Link to="/contact">Contact us</Link></Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
